/**
 * The resumable job queue (BENCH-V2-SPEC §12): a second, small IndexedDB database, opened from the main thread (the bench
 * orchestrator's own domain: the analysis worker's `provenance` database is untouched). One record per job, version 2; the
 * upgrade from version 1 drops the v1 resume fields (`migrateJob`). Version 3 adds the `briefs` store: one document brief per
 * document hash (BENCH-V3-PLAN §8), so a re-run or a new job on the same text skips the brief's calls. Version 4 moves the job
 * records to schema 3 (the `versions` setting, BENCH-V3-PLAN §6): every older record is migrated once, on the upgrade.
 * Version 5 adds the `chains` store: one version chain per document, keyed by version 0's hash (DESIGN UD-27, `chain.ts`).
 * Best effort throughout: storage can be missing or full.
 */
import type { DocumentBrief } from '../../engine/bench/brief.ts';
import { briefKey } from '../../engine/bench/brief.ts';
import { type DocChain, migrateChain } from './chain.ts';
import { type BenchJob, mergeJob, migrateJob, reconcileJob, runsElsewhere } from './job.ts';
import { heldJobIds, readSeen } from './run-watch.ts';

export type { BenchJob, JobStatus } from './job.ts';

const DB_NAME = 'provenance-bench';
export const DB_VERSION = 5;

function open(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		if (typeof indexedDB === 'undefined') return reject(new Error('IndexedDB is unavailable'));
		const req = indexedDB.open(DB_NAME, DB_VERSION);
		req.onupgradeneeded = (event) => {
			const db = req.result;
			if (!db.objectStoreNames.contains('jobs')) db.createObjectStore('jobs', { keyPath: 'id' });
			if (!db.objectStoreNames.contains('briefs'))
				db.createObjectStore('briefs', { keyPath: 'key' });
			if (!db.objectStoreNames.contains('chains'))
				db.createObjectStore('chains', { keyPath: 'root' });
			// version 1 records lose their resume fields; schema 2 records (database versions 2 and 3) become schema 3
			if (event.oldVersion >= 1 && req.transaction) {
				const store = req.transaction.objectStore('jobs');
				const cursor = store.openCursor();
				cursor.onsuccess = () => {
					const c = cursor.result;
					if (!c) return;
					c.update(migrateJob(c.value));
					c.continue();
				};
			}
		};
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
		req.onblocked = () => reject(new Error('IndexedDB is blocked'));
	});
}

function finished(tx: IDBTransaction): Promise<void> {
	return new Promise((resolve, reject) => {
		tx.oncomplete = () => resolve();
		tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
		tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
	});
}

/** Writes a job over its stored record; the record's `createdAt` is kept. */
export async function putJob(job: BenchJob): Promise<boolean> {
	try {
		const db = await open();
		const tx = db.transaction('jobs', 'readwrite');
		const store = tx.objectStore('jobs');
		const get = store.get(job.id);
		get.onsuccess = () => {
			store.put(mergeJob(get.result as BenchJob | undefined, job));
		};
		await finished(tx);
		db.close();
		return true;
	} catch {
		return false;
	}
}

export async function getJob(id: string): Promise<BenchJob | undefined> {
	try {
		const db = await open();
		const job = await new Promise<BenchJob | undefined>((resolve, reject) => {
			const r = db.transaction('jobs').objectStore('jobs').get(id);
			r.onsuccess = () => resolve(r.result as BenchJob | undefined);
			r.onerror = () => reject(r.error);
		});
		db.close();
		return job;
	} catch {
		return undefined;
	}
}

export async function removeJob(id: string): Promise<void> {
	try {
		const db = await open();
		const tx = db.transaction('jobs', 'readwrite');
		tx.objectStore('jobs').delete(id);
		await finished(tx);
		db.close();
	} catch {
		/* nothing stored, nothing to remove */
	}
}

/**
 * Every stored job, newest first, and the ids of the Standard jobs another page is running (UD-38). A Standard job stored as
 * `running` reads as `paused`, with the reason the page left behind (`reconcileJob`), unless a page holds its run lock: this
 * one (`liveId`, the job this page is running right now) or another tab (listed in `elsewhere`, and left `running`).
 */
export async function listJobsDetailed(
	liveId: string | null = null
): Promise<{ jobs: BenchJob[]; elsewhere: Set<string> }> {
	try {
		const db = await open();
		const all = await new Promise<BenchJob[]>((resolve, reject) => {
			const r = db.transaction('jobs').objectStore('jobs').getAll();
			r.onsuccess = () => resolve(r.result as BenchJob[]);
			r.onerror = () => reject(r.error);
		});
		db.close();
		const held = await heldJobIds();
		const jobs = all
			.map((j) => (j.id === liveId ? j : reconcileJob(j, held, readSeen(j.id))))
			.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
		const elsewhere = new Set(
			jobs.filter((j) => j.id !== liveId && runsElsewhere(j, held)).map((j) => j.id)
		);
		return { jobs, elsewhere };
	} catch {
		return { jobs: [], elsewhere: new Set() };
	}
}

/** Every stored job, newest first (`listJobsDetailed` without the ids of those running in another tab). */
export async function listJobs(liveId: string | null = null): Promise<BenchJob[]> {
	return (await listJobsDetailed(liveId)).jobs;
}

/** The cached brief of a document (`docHash`), or undefined: none stored, or the store is unavailable. */
export async function getBrief(docHash: string): Promise<DocumentBrief | undefined> {
	try {
		const db = await open();
		const row = await new Promise<{ key: string; brief: DocumentBrief } | undefined>(
			(resolve, reject) => {
				const r = db.transaction('briefs').objectStore('briefs').get(briefKey(docHash));
				r.onsuccess = () => resolve(r.result as { key: string; brief: DocumentBrief } | undefined);
				r.onerror = () => reject(r.error);
			}
		);
		db.close();
		return row?.brief;
	} catch {
		return undefined;
	}
}

export async function putBrief(docHash: string, brief: DocumentBrief): Promise<boolean> {
	try {
		const db = await open();
		const tx = db.transaction('briefs', 'readwrite');
		tx.objectStore('briefs').put({ key: briefKey(docHash), brief });
		await finished(tx);
		db.close();
		return true;
	} catch {
		return false;
	}
}

/** The version chain of a document (version 0's hash), or undefined: none stored, or the store is unavailable. */
export async function getChain(root: string): Promise<DocChain | undefined> {
	try {
		const db = await open();
		const chain = await new Promise<DocChain | undefined>((resolve, reject) => {
			const r = db.transaction('chains').objectStore('chains').get(root);
			r.onsuccess = () => resolve(migrateChain(r.result));
			r.onerror = () => reject(r.error);
		});
		db.close();
		return chain;
	} catch {
		return undefined;
	}
}

export async function putChain(chain: DocChain): Promise<boolean> {
	try {
		const db = await open();
		const tx = db.transaction('chains', 'readwrite');
		tx.objectStore('chains').put(chain);
		await finished(tx);
		db.close();
		return true;
	} catch {
		return false;
	}
}

export async function removeChain(root: string): Promise<void> {
	try {
		const db = await open();
		const tx = db.transaction('chains', 'readwrite');
		tx.objectStore('chains').delete(root);
		await finished(tx);
		db.close();
	} catch {
		/* nothing stored, nothing to remove */
	}
}

export function newJobId(): string {
	return `j${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}
